import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../app/auth';
import { ApiError, describeError } from '../lib/api';
import { Button, Field, Input, Notice } from '../components/ui';

/**
 * 员工登录。
 *
 * 两步验证走同一条链路：口令正确但需要验证码时，服务端返回 `totp_required`，
 * 就地展开验证码输入，而不是让用户猜「该填哪种表单」。
 */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [needsCode, setNeedsCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function submit() {
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      const session = await login(email.trim(), password, code.trim());
      navigate(session.mustChangePassword ? '/account' : '/', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.code === 'totp_required') {
          setNeedsCode(true);
          setError('请输入两步验证码');
        } else {
          setFieldErrors(caught.fieldErrors);
          setError(caught.message);
        }
      } else {
        setError(describeError(caught));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="console-wash flex min-h-full items-center justify-center px-4 py-10">
      <div className="w-full max-w-[380px]">
        <div className="mb-6">
          <p className="text-[15px] font-semibold text-fg">Havorix™ 运营后台</p>
          <p className="mt-1 text-xs text-fg-subtle">仅限内部员工使用，登录行为会被记录</p>
        </div>

        <h1 className="mb-5 text-[26px] font-semibold text-fg">登录</h1>

        {error ? (
          <div className="mb-4">
            <Notice tone="danger">{error}</Notice>
          </div>
        ) : null}

        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <Field label="邮箱" htmlFor="email" error={fieldErrors.email}>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="ops@havorix.cn"
              autoComplete="username"
            />
          </Field>

          <Field label="口令" htmlFor="password" error={fieldErrors.password}>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
            />
          </Field>

          {needsCode ? (
            <Field label="两步验证码" htmlFor="code" error={fieldErrors.code} hint="验证器 App 里的 6 位数字">
              <Input
                id="code"
                value={code}
                onChange={(value) => setCode(value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                autoComplete="one-time-code"
              />
            </Field>
          ) : null}

          <Button type="submit" variant="primary" block loading={busy}>
            登录
          </Button>
        </form>

        <p className="mt-6 text-xs text-fg-subtle">
          没有账号？由超级管理员在「员工」页创建。忘记口令请联系超级管理员重置。
        </p>
      </div>
    </div>
  );
}
